import React from 'react';

const JOURNEY_STATS = [
  { label: 'Learners started',        value: 'Starting now' },
  { label: 'First lessons completed', value: 'Starting now' },
  { label: 'Checkpoints passed',      value: 'Starting now' },
  { label: 'Gaps repaired',           value: 'Starting now' },
  { label: 'Study streaks active',    value: 'Starting now' },
  { label: 'Real outcomes reported',  value: '0'            },
];

const PROGRESS_TIERS = [
  {
    label: 'Started the path',
    desc: 'Tried the demo or began CompTIA A+.',
    highlight: false,
  },
  {
    label: 'First win',
    desc: 'Completed the first lesson.',
    highlight: false,
  },
  {
    label: 'Gap repaired',
    desc: 'Missed something, received remediation, passed after repair.',
    highlight: false,
  },
  {
    label: 'Momentum built',
    desc: 'Completed multiple lessons or maintained a study streak.',
    highlight: false,
  },
  {
    label: 'Outcome changed',
    desc: 'Certification, interview, job, promotion, raise, or career transition. This is what 1,000 means.',
    highlight: true,
  },
];

const JOURNEY_LOG = [
  {
    date: 'June 1, 2026',
    entry: 'Launch journey opened — CompTIA A+ and Network+ are the first paths.',
  },
  {
    date: 'June 1, 2026',
    entry:
      'The first public goal is simple: help learners get through lesson one, not just sign up.',
  },
  {
    date: 'June 1, 2026',
    entry:
      'Every checkpoint is designed to detect what was missed, repair it, and advance only after mastery.',
  },
];

export default function FollowJourneyPage({ onNavigate }) {
  // App.jsx routes by full path reload (no React Router); other screens use plain
  // anchors. Keep the onNavigate hook for future routing layers but fall back to
  // window.location.href so the buttons work today.
  const navigate = (path) => {
    if (typeof onNavigate === 'function') {
      onNavigate(path);
    } else {
      window.location.href = path;
    }
  };

  // CSS-variable names align with src/index.css; fallbacks match the dark-theme
  // palette so the page renders before the theme attribute is applied.
  const styles = {
    page: {
      minHeight: '100vh',
      backgroundColor: 'var(--bg-primary, #0f0f12)',
      color: 'var(--text-primary, #e8e6e1)',
      fontFamily: "'Hanken Grotesk', 'Inter', system-ui, sans-serif",
      padding: '0',
    },
    container: {
      maxWidth: '720px',
      margin: '0 auto',
      padding: '0 24px',
    },

    // ── Hero ──────────────────────────────────────────────────
    hero: {
      paddingTop: '80px',
      paddingBottom: '72px',
      textAlign: 'center',
    },
    eyebrow: {
      fontSize: '13px',
      fontWeight: '600',
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      color: 'var(--accent-primary, #2dd4bf)',
      marginBottom: '20px',
    },
    headline: {
      fontFamily: "'Fraunces', serif",
      fontSize: 'clamp(28px, 5vw, 44px)',
      fontWeight: '500',
      lineHeight: '1.15',
      marginBottom: '20px',
      color: 'var(--text-primary, #e8e6e1)',
      letterSpacing: '-0.02em',
    },
    subheadline: {
      fontSize: '17px',
      lineHeight: '1.7',
      color: 'var(--text-secondary, #a8a49e)',
      maxWidth: '580px',
      margin: '0 auto 40px',
    },
    ctaRow: {
      display: 'flex',
      gap: '16px',
      justifyContent: 'center',
      flexWrap: 'wrap',
    },
    btnPrimary: {
      padding: '14px 32px',
      borderRadius: '8px',
      border: 'none',
      background: 'var(--accent-primary, #2dd4bf)',
      color: 'var(--accent-text, #0f0f12)',
      fontSize: '15px',
      fontWeight: '700',
      cursor: 'pointer',
      fontFamily: 'inherit',
      transition: 'opacity 0.15s',
    },
    btnSecondary: {
      padding: '14px 32px',
      borderRadius: '8px',
      border: '1px solid var(--border-medium, rgba(255,255,255,0.10))',
      background: 'transparent',
      color: 'var(--text-secondary, #a8a49e)',
      fontSize: '15px',
      fontWeight: '500',
      cursor: 'pointer',
      fontFamily: 'inherit',
      transition: 'opacity 0.15s',
    },

    // ── Section shared ────────────────────────────────────────
    section: {
      paddingTop: '64px',
      paddingBottom: '64px',
      borderTop: '1px solid var(--border-soft, rgba(255,255,255,0.06))',
    },
    sectionLabel: {
      fontSize: '11px',
      fontWeight: '700',
      letterSpacing: '0.14em',
      textTransform: 'uppercase',
      color: 'var(--accent-primary, #2dd4bf)',
      marginBottom: '12px',
    },
    sectionHeadline: {
      fontFamily: "'Fraunces', serif",
      fontSize: '22px',
      fontWeight: '500',
      marginBottom: '8px',
      color: 'var(--text-primary, #e8e6e1)',
    },
    sectionBody: {
      fontSize: '15px',
      lineHeight: '1.7',
      color: 'var(--text-secondary, #a8a49e)',
      marginBottom: '40px',
    },

    // ── Stats grid ────────────────────────────────────────────
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
      gap: '16px',
      marginTop: '32px',
    },
    statCard: {
      background: 'var(--bg-secondary, #1a1a21)',
      border: '1px solid var(--border-soft, rgba(255,255,255,0.06))',
      borderRadius: '10px',
      padding: '24px 20px',
      textAlign: 'center',
    },
    statValue: {
      fontSize: '22px',
      fontWeight: '700',
      color: 'var(--text-primary, #e8e6e1)',
      marginBottom: '6px',
    },
    statLabel: {
      fontSize: '12px',
      color: 'var(--text-secondary, #a8a49e)',
      lineHeight: '1.4',
    },

    // ── Progress tiers ────────────────────────────────────────
    tierList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      marginTop: '32px',
    },
    tier: {
      display: 'flex',
      gap: '16px',
      alignItems: 'flex-start',
      padding: '20px',
      borderRadius: '10px',
      background: 'var(--bg-secondary, #1a1a21)',
      border: '1px solid var(--border-soft, rgba(255,255,255,0.06))',
    },
    tierHighlight: {
      border: '1px solid var(--accent-primary, #2dd4bf)',
      background: 'var(--accent-soft, rgba(45,212,191,0.12))',
    },
    tierDot: {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      background: 'var(--text-muted, #8f8b82)',
      marginTop: '7px',
      flexShrink: 0,
    },
    tierDotHighlight: {
      background: 'var(--accent-primary, #2dd4bf)',
    },
    tierLabel: {
      fontSize: '14px',
      fontWeight: '600',
      color: 'var(--text-primary, #e8e6e1)',
      marginBottom: '4px',
    },
    tierDesc: {
      fontSize: '13px',
      color: 'var(--text-secondary, #a8a49e)',
      lineHeight: '1.5',
    },

    // ── Journey log ───────────────────────────────────────────
    logList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      marginTop: '32px',
    },
    logCard: {
      padding: '20px 24px',
      borderRadius: '10px',
      background: 'var(--bg-secondary, #1a1a21)',
      border: '1px solid var(--border-soft, rgba(255,255,255,0.06))',
    },
    logDate: {
      fontSize: '11px',
      fontWeight: '600',
      letterSpacing: '0.08em',
      color: 'var(--accent-primary, #2dd4bf)',
      marginBottom: '8px',
      textTransform: 'uppercase',
    },
    logEntry: {
      fontSize: '14px',
      lineHeight: '1.65',
      color: 'var(--text-secondary, #a8a49e)',
    },

    // ── Bottom CTA ────────────────────────────────────────────
    bottomCta: {
      textAlign: 'center',
      paddingTop: '72px',
      paddingBottom: '96px',
      borderTop: '1px solid var(--border-soft, rgba(255,255,255,0.06))',
    },
    bottomCtaHeadline: {
      fontFamily: "'Fraunces', serif",
      fontSize: '26px',
      fontWeight: '500',
      marginBottom: '16px',
      color: 'var(--text-primary, #e8e6e1)',
    },
    bottomCtaBody: {
      fontSize: '16px',
      lineHeight: '1.7',
      color: 'var(--text-secondary, #a8a49e)',
      maxWidth: '520px',
      margin: '0 auto 36px',
    },
  };

  return (
    <div style={styles.page}>
      <div style={styles.container}>

        {/* ── Hero ── */}
        <section style={styles.hero}>
          <p style={styles.eyebrow}>Follow the Journey</p>
          <h1 style={styles.headline}>
            1,000 lives changed through governed adaptive learning.
          </h1>
          <p style={styles.subheadline}>
            AdaptOS is being built for people studying after work, after a shift, or after life already took most of their energy. Follow the public journey as learners start, get stuck, repair gaps, pass checkpoints, and move toward a better career.
          </p>
          <div style={styles.ctaRow}>
            <button style={styles.btnPrimary} onClick={() => navigate('/registration')}>
              Try one lesson
            </button>
            <button style={styles.btnSecondary} onClick={() => navigate('/affiliate')}>
              Become an affiliate
            </button>
          </div>
        </section>

        {/* ── Mission statement ── */}
        <section style={styles.section}>
          <p style={styles.sectionLabel}>The Mission</p>
          <h2 style={styles.sectionHeadline}>Where we&rsquo;re headed.</h2>
          <p style={styles.sectionBody}>
            Current mission: help 1,000 people build a new path through certification learning that remembers where they left off, knows what they missed, repairs the gap, and moves them forward. Every learner who earns a certification, lands an interview, or changes their career counts as one.
          </p>
        </section>

        {/* ── Stats ── */}
        <section style={styles.section}>
          <p style={styles.sectionLabel}>Journey Progress</p>
          <h2 style={styles.sectionHeadline}>Honest numbers, from the start.</h2>
          <p style={styles.sectionBody}>
            These counters track real progress &mdash; not signups, not impressions. Updated manually as milestones are verified.
          </p>
          <div style={styles.statsGrid}>
            {JOURNEY_STATS.map((s) => (
              <div key={s.label} style={styles.statCard}>
                <div style={styles.statValue}>{s.value}</div>
                <div style={styles.statLabel}>{s.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ── What counts as progress ── */}
        <section style={styles.section}>
          <p style={styles.sectionLabel}>What Progress Means</p>
          <h2 style={styles.sectionHeadline}>Not all progress is equal.</h2>
          <p style={styles.sectionBody}>
            The mission counts 1,000 outcomes &mdash; not 1,000 signups. Here&rsquo;s how progress is defined, from first step to life change.
          </p>
          <div style={styles.tierList}>
            {PROGRESS_TIERS.map((t) => (
              <div
                key={t.label}
                style={{ ...styles.tier, ...(t.highlight ? styles.tierHighlight : {}) }}
              >
                <div
                  style={{
                    ...styles.tierDot,
                    ...(t.highlight ? styles.tierDotHighlight : {}),
                  }}
                />
                <div>
                  <div style={styles.tierLabel}>{t.label}</div>
                  <div style={styles.tierDesc}>{t.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── Journey log ── */}
        <section style={styles.section}>
          <p style={styles.sectionLabel}>Journey Log</p>
          <h2 style={styles.sectionHeadline}>What&rsquo;s happened so far.</h2>
          <p style={styles.sectionBody}>
            Real entries only. No simulated learner stories.
          </p>
          <div style={styles.logList}>
            {JOURNEY_LOG.map((log, i) => (
              <div key={i} style={styles.logCard}>
                <div style={styles.logDate}>{log.date}</div>
                <div style={styles.logEntry}>{log.entry}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ── Bottom CTA ── */}
        <section style={styles.bottomCta}>
          <h2 style={styles.bottomCtaHeadline}>Start with one lesson.</h2>
          <p style={styles.bottomCtaBody}>
            No bootcamp pressure. No hype. Just one structured lesson that teaches, checks, repairs, and helps you keep moving. Mastery before moving forward &mdash; every time.
          </p>
          <button style={styles.btnPrimary} onClick={() => navigate('/registration')}>
            Start with one lesson
          </button>
        </section>

      </div>
    </div>
  );
}
